import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { getApiData, postFormData } from "../../../../../utils/APIHelper";
import ProjectUpdateForm from "../ProjectUpdateForm";
import ActionBar from "../components/ActionBar";
import Permission from "../../../../../components/Permission";
import { ReactComponent as LeftArrowIcon } from "../../../../../assets/svg/LeftArrowIcon.svg";

export default function UpdateDetails({
  projectId,
  projectDetails,
  isMobile,
  styles: passedStyles = {},
  listData = [],
  refetchList,
  detailsPath,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateId = searchParams.get("updateId");
  const updateDate = searchParams.get("updateDate");
  const isCreateMode = updateId === "new";
  const isEdit = !isCreateMode && !!updateId;

  const selectedRow =
    !isCreateMode && updateId
      ? listData?.find((i) => String(i.update_id) === String(updateId))
      : null;

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedMilestones, setSelectedMilestones] = useState([]);
  const [milestoneProgress, setMilestoneProgress] = useState({});
  const [milestoneNotes, setMilestoneNotes] = useState({});
  const [loader, setLoader] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [createdUpdateId, setCreatedUpdateId] = useState(null);
  const [singleUpdateData, setSingleUpdateData] = useState(null);

  const effectiveUpdateId = isCreateMode ? createdUpdateId : updateId;
  const hasUpdateId = !!effectiveUpdateId;
  const refData = singleUpdateData?.ref_data ?? [];
  const selectedRowResolved =
    selectedRow ||
    (hasUpdateId && isCreateMode
      ? { update_id: Number(effectiveUpdateId) }
      : null);

  const form = useForm({ defaultValues: {} });
  const { reset } = form;

  useEffect(() => {
    if (selectedRow && !isCreateMode) {
      reset({
        name: selectedRow.update_name,
        date: selectedRow.update_date,
        description: selectedRow.message,
      });
    }
  }, [selectedRow, isCreateMode, reset]);

  useEffect(() => {
    if (!hasUpdateId || !effectiveUpdateId) {
      setSingleUpdateData(null);
      return;
    }
    let cancelled = false;
    getApiData(`api/project/update/${effectiveUpdateId}`, "GET")
      .then((response) => {
        if (cancelled) return;
        if (response?.success && response?.data?.length > 0) {
          setSingleUpdateData(response.data[0]);
        } else {
          setSingleUpdateData(null);
        }
      })
      .catch(() => {
        if (!cancelled) setSingleUpdateData(null);
      });
    return () => { cancelled = true; };
  }, [hasUpdateId, effectiveUpdateId]);

  const listPath = detailsPath
    ? detailsPath.replace(/\/details$/, "")
    : `/project/details/${projectId}/project-updates`;

  const handleBackToList = () => {
    setCreatedUpdateId(null);
    navigate(listPath, { replace: true });
    if (refetchList) refetchList();
  };

  const TAB_ORDER = ["summary", "activities", "milestones", "issues"];
  const getNextTab = (current) => {
    const i = TAB_ORDER.indexOf(current);
    if (i === -1 || i >= TAB_ORDER.length - 1) return null;
    return TAB_ORDER[i + 1];
  };

  const handleSummarySubmit = form.handleSubmit(async (formData) => {
    const plannedProgressById =
      (projectDetails?.milestone || []).reduce((acc, milestone) => {
        if (milestone?.id != null) {
          acc[milestone.id] = milestone.milestone_percentage ?? 0;
        }
        return acc;
      }, {}) || {};

    const basePayload = {
      data: [],
      date: moment(formData.date).format("YYYY-MM-DD"),
      message: formData.description,
      name: formData.name,
    };

    if (isCreateMode && !createdUpdateId) {
      const formDataObj = new FormData();
      Object.keys(basePayload).forEach((key) => {
        if (Array.isArray(basePayload[key])) {
          formDataObj.append(key, JSON.stringify(basePayload[key]));
        } else {
          formDataObj.append(key, basePayload[key]);
        }
      });

      try {
        setLoader(true);
        const res = await postFormData(
          `api/project/${projectId}/updates`,
          formDataObj
        );
        const newId =
          res?.data?.update_id ?? res?.data?.id ?? res?.update_id;

        if (res?.success && newId) {
          setCreatedUpdateId(String(newId));
          const newParams = new URLSearchParams(searchParams);
          newParams.set("updateId", newId);
          const dateStr = formData.date
            ? moment(formData.date).format("YYYY-MM-DD")
            : "";
          if (dateStr) newParams.set("updateDate", dateStr);
          navigate(`${detailsPath || window.location.pathname}?${newParams.toString()}`, {
            replace: true,
          });
          setActiveTab("activities");
          if (refetchList) refetchList();
          toast.success(res?.message || "Progress report created.");
        } else {
          toast.error(res?.message || "Could not create progress report");
        }
      } catch (error) {
        toast.error(error?.message || "Could not create progress report");
      } finally {
        setLoader(false);
      }
      return;
    }

    if (isCreateMode && createdUpdateId) {
      const refDataForCreate = singleUpdateData?.ref_data ?? [];
      const sanitizedMilestones = selectedMilestones.map(
        ({ budget, ...rest }) => {
          const note = (milestoneNotes[rest.id] ?? "").trim();
          const refItem = refDataForCreate.find((r) => r.id === rest.id || r.id == rest.id);
          const previous_progress =
            refItem?.progress ?? rest.progress ?? 0;
          const progress =
            milestoneProgress[rest.id] ?? refItem?.progress ?? rest.progress ?? 0;
          return {
            ...rest,
            previous_progress,
            progress,
            planned_progress: plannedProgressById[rest.id] ?? 0,
            ...(note ? { notes: note } : {}),
          };
        }
      );

      try {
        setLoader(true);
        const response = await getApiData(
          `api/project/update/${createdUpdateId}`,
          "PUT",
          { ...basePayload, data: sanitizedMilestones },
          { "Content-Type": "application/json" }
        );
        if (response?.success) {
          toast.success(response?.message || "Saved successfully");
          if (refetchList) refetchList();
          if (activeTab !== "issues") {
            const nextTab = getNextTab(activeTab);
            if (nextTab) setActiveTab(nextTab);
          } else {
            handleBackToList();
          }
        } else {
          toast.error(response?.message || "Update failed");
        }
      } catch (error) {
        toast.error(error?.message || "Update failed");
      } finally {
        setLoader(false);
      }
      return;
    }

    const sanitizedMilestones = selectedMilestones.map(({ budget, ...rest }) => {
      const note = (milestoneNotes[rest.id] ?? "").trim();
      const refItem = (refData || []).find((r) => r.id === rest.id || r.id == rest.id);
      const previous_progress =
        refItem?.progress ?? rest.progress ?? 0;
      const progress =
        milestoneProgress[rest.id] ?? refItem?.progress ?? rest.progress ?? 0;
      return {
        ...rest,
        previous_progress,
        progress,
        planned_progress: plannedProgressById[rest.id] ?? 0,
        ...(note ? { notes: note } : {}),
      };
    });

    try {
      setLoader(true);
      const response = await getApiData(
        `api/project/update/${updateId}`,
        "PUT",
        {
          data: sanitizedMilestones,
          date: moment(formData.date).format("YYYY-MM-DD"),
          message: formData.description,
          name: formData.name,
        },
        { "Content-Type": "application/json" }
      );

      if (!response?.success) {
        toast.error(response?.message || "Update failed");
        return;
      }

      const newFiles = (uploadedFiles || []).filter((f) => !f.id && f.file);
      for (let i = 0; i < newFiles.length; i++) {
        const f = newFiles[i];
        const fd = new FormData();
        fd.append("ref_id", updateId);
        fd.append("ref_type", "PROJECT_UPDATE");
        fd.append("type", "");
        fd.append("file_1", f.file);
        fd.append(
          "meta_data",
          JSON.stringify(f.meta_data || { room: null, milestones: [] })
        );
        fd.append("tag", "");
        await postFormData("api/project/updates/files", fd);
      }

      toast.success(
        response?.message || "Project update changed successfully."
      );
      if (refetchList) refetchList();
      if (activeTab !== "issues") {
        const nextTab = getNextTab(activeTab);
        if (nextTab) setActiveTab(nextTab);
      } else {
        handleBackToList();
      }
    } catch (error) {
      toast.error(error?.message || "Update failed");
    } finally {
      setLoader(false);
    }
  });

  const displayDate =
    isEdit && (updateDate || selectedRow?.update_date)
      ? moment(updateDate || selectedRow?.update_date).format("MMM DD, YYYY")
      : null;

  return (
    <Permission
      resource="MILESTONE_UPDATES"
      action="READ"
      fallback={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "256px",
          }}
        >
          <p style={{ color: "#757575", margin: 0 }}>
            You don't have permission to view project updates.
          </p>
        </div>
      }
    >
      <div style={passedStyles.container || {}}>
        <header style={passedStyles.header}>
          <div style={passedStyles.headerContent}>
            <LeftArrowIcon
              onClick={handleBackToList}
              style={{ cursor: "pointer" }}
            />
            <div
              style={{
                ...(passedStyles.idText || {}),
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Progress Report
              {displayDate != null && (
                <span style={{ marginLeft: "8px", color: "#949DAA", fontWeight: 400 }}>
                  - {displayDate}
                </span>
              )}
            </div>
          </div>
        </header>

        <div style={passedStyles.subContainer || { padding: "2vh 2vw" }}>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <ProjectUpdateForm
              form={form}
              projectId={projectId}
              selectedRow={selectedRowResolved}
              createdUpdateId={createdUpdateId}
              handleBackToList={handleBackToList}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              selectedMilestones={selectedMilestones}
              setSelectedMilestones={setSelectedMilestones}
              milestoneProgress={milestoneProgress}
              setMilestoneProgress={setMilestoneProgress}
              milestoneNotes={milestoneNotes}
              setMilestoneNotes={setMilestoneNotes}
              loading={loader}
              isViewMode={isViewMode}
              setIsViewMode={setIsViewMode}
              hasUpdateId={hasUpdateId}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isCreateMode={isCreateMode}
              projectDetails={projectDetails}
            />

            {!isViewMode && activeTab !== "activities" && (
              <Permission
                resource="MILESTONE_UPDATES"
                action={isCreateMode ? "CREATE" : "UPDATE"}
              >
                <ActionBar
                  nextText={
                    activeTab === "issues"
                      ? "Submit"
                      : isCreateMode && !createdUpdateId
                        ? "Save & continue"
                        : "Next"
                  }
                  onSubmit={handleSummarySubmit}
                  disableNext={loader}
                />
              </Permission>
            )}
          </div>
        </div>
      </div>
    </Permission>
  );
}
